import { formatMoney, parseMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { useSessionStorage } from '@betterment/js-runtime';
import {
  Button,
  FormikField as Field,
  GuidanceBox,
  H1,
  H3,
  HStack,
  Input,
  Text,
  VStack,
} from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { ReviewProfileSheet } from './components/ReviewProfileSheet';

import { IdempotencyProviderWithFeatureRegistry } from '../../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import Footer from '../../../../Shared/components-v2/footer/Footer';
import { convertDisplayableErrors } from '../../../../Shared/displayable-errors';
import { ProductCategory } from '../../../data';
import { useCompleteDraftPurposeFlow } from '../../hooks/useCompleteDraftPurposeFlow';
import { desiredSpending, useRetirementStep } from '../FlowConfig';
import { RetirementPurposeCreationOptionDocument } from '../graphql/query.retirementPurposeCreationOption.generated';

type FormValues = Yup.InferType<typeof desiredSpending>;

export const DesiredSpendingStep: React.FC = () => {
  const navigate = useNavigate();
  const { dependencies: retirementSelectionDependencies } = useRetirementStep('retirementSelection');
  const { commit, dependencies } = useRetirementStep('desiredSpending');
  const { flowId } = useParams<{ flowId: string }>();

  const { data, refetch } = useGraphQLQuery(RetirementPurposeCreationOptionDocument, {
    throwOnError: true,
    fetchPolicy: 'network-only',
  });

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  const { completeFlow, errors: displayableErrors } = useCompleteDraftPurposeFlow({
    draftEnvelopeId: flowId,
    name: retirementSelectionDependencies.nameAndImage.name,
    imageType: retirementSelectionDependencies.nameAndImage.imageType,
    productCategory,
  });

  if (!data) return null;

  const retirementGrowthOption = data.purposeCreationOptions.filter(
    option => option.__typename === 'RetirementGrowthPurposeCreationOption'
  )[0];

  const { defaultRetirementMonthlySpending, recommendedMonthlySpending } = retirementGrowthOption;

  const initialValues: FormValues = {
    desiredMonthlySpending: formatMoney(defaultRetirementMonthlySpending, 'raw'),
  };

  const handleSubmit = async (formValues: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    commit(formValues);

    await completeFlow({
      purposeSubType: 'retirement_growth',
      purposeInput: {
        retirementGrowth: {
          retirementAge: dependencies.retirementDetails.retirementAge,
          retirementZip: dependencies.retirementDetails.retirementZipCode,
          targetMonthlySpendingAmount: parseMoney(formValues.desiredMonthlySpending),
        },
      },
    });

    if (displayableErrors.length > 0) {
      formikHelpers.setErrors(convertDisplayableErrors(displayableErrors));
    }
  };

  return (
    <WorkflowLayout pageTitle="Retirement">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>How much do you want to spend each month in retirement?</H1>
            <Text>We'll use this to help you figure out how much you need to save in total.</Text>
          </VStack>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={desiredSpending}
            validateOnChange={false}
          >
            <Form>
              <VStack css={{ gap: 'space.400' }}>
                <VStack css={{ gap: 'space.200' }}>
                  <Field name="desiredMonthlySpending">
                    <Field.Label>Desired monthly spending</Field.Label>
                    <Field.Control>
                      <Input mask="number" leadingSymbol="$" trailingSymbol="/month" />
                    </Field.Control>
                    <Field.ErrorMessage />
                  </Field>
                  <GuidanceBox css={{ maxWidth: '100%' }}>
                    <GuidanceBox.Icon />
                    {recommendedMonthlySpending.title && (
                      <GuidanceBox.Header>
                        <H3 variant="title100">{recommendedMonthlySpending.title}</H3>
                      </GuidanceBox.Header>
                    )}
                    <GuidanceBox.Content css={{ display: 'grid', gap: 'space.100' }}>
                      <Text variant="text50" dangerouslySetInnerHTML={{ __html: recommendedMonthlySpending.body }} />
                    </GuidanceBox.Content>
                  </GuidanceBox>
                  <HStack css={{ alignItems: 'center', gap: 'space.50' }}>
                    <Text color="text.weak" variant="text50">
                      Does your info look correct?
                    </Text>
                    <IdempotencyProviderWithFeatureRegistry featureName="financialProfile">
                      <ReviewProfileSheet
                        annualPretaxIncome={data.financialProfile?.annualPretaxIncome}
                        taxFilingStatus={data.financialProfile?.taxFilingStatus}
                        spouse={data.financialProfile?.spouse}
                        updateData={refetch}
                      />
                    </IdempotencyProviderWithFeatureRegistry>
                  </HStack>
                </VStack>

                <Button type="submit" css={{ width: 'fit-content' }}>
                  Continue
                </Button>
              </VStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
